/**
 * Damage calculation: blast radius, health reduction, kill detection, craters.
 * Pure functions — no side effects.
 */

import { throughArmour } from './armour';
import type { GameState, PlayerState, PlanetState, StructureState, HazardState, RuinState, PlantedFlagState, CrashedDropCanState, FireZone, Vec3, FloraState, CollectibleNode, CarryableObjectState, GroundItemState } from '../types';
import { WEAPONS, KNOCKBACK_FORCE, KNOCKBACK_FALLOFF, KNOCKBACK_RADIUS_FACTOR, KNOCKBACK_UPWARD_BIAS, KNOCKBACK_MIN_LAUNCH_STRENGTH, FALL_DAMAGE_FACTOR, MAX_TERRAIN_HEIGHT_FRACTION, MAX_CRATER_DEPTH_FRACTION } from '../constants';
import type { WeaponKey, ItemKey, PropEvent } from '../types';
import type { BlowCause, Material, SeatDef } from '../types/body';
import type { StructureReach } from './blastReach';
import { hatchSurvival } from './body';
import { DAMAGE_VS, PROPS, VEHICLE_BLAST } from '../constants/damage';
import { GROUND_DAMAGE, HAZARD_GROUND } from '../constants/ground';
import { BODIES, type BodyKey } from '../constants/body';
import { endDrone } from './drones';
import { DRONE_HITS, PERSON_BODY_HEIGHT } from './scaleProfile';
import { treeForm, shedLimbs, settleDebris, debrisLandsOn } from './debris';
import { blastParts } from './parts';
import { zonesOn, zoneTableOf, zoneOf, burstOnSeat, hullShare, knockedOut, type BurstZones } from './vehicleZones';
import { vecLength, vecSub, vecNormalize, vecDot, vecAdd, vecScale } from '../math';
import { getSurfaceRadius } from './surface';
import { rimBetween, coverKnockbackFactor, coverDamageFactor } from './cover';
import { propHitBonus, STRUCTURE_HALF_WIDTH, HAZARD_HALF_WIDTH } from './propScale';
import { computePlanetVertexColors, recolorVertices, accumulateScorch, hexToRgb } from './planetColors';
import { dacos, dpow } from './dmath';
import { verticesNear } from './vertexCells';
import { bodyStanceOf } from './stance';
export { hexToRgb };

// =============================================================================
// DAMAGE CALCULATION
// =============================================================================

export interface DamageResult {
  players: PlayerState[];
  kills: number[];  // IDs of killed players
  damages: Array<{ playerId: number; damage: number }>;
  knockbacks: Array<{ playerId: number; launched: boolean; sheltered?: boolean }>; // Players knocked back, launched = knocked off planet; sheltered = a rim cut the knockback (37.18)
  structures: StructureState[];
  destroyedStructures: number[];  // IDs of destroyed structures
  hazards: HazardState[];
  destroyedHazards: number[];  // IDs of destroyed hazards
  plantedFlags: PlantedFlagState[];
  destroyedFlags: number[];  // IDs of destroyed flags
  crashedDropCans: CrashedDropCanState[];
  destroyedDropCans: number[];  // IDs of destroyed drop cans
}

// =============================================================================
// THE ONE DAMAGE DOOR (R12 step 4, ARCHITECTURE §5.13)
// =============================================================================

/**
 * What hurt something: a blow (63.4's table scales it by what it hits, constants/damage.ts DAMAGE_VS) or a death that
 * is not one. A blast (a burst too) and a bullet are armored on a unit (a flat amount off the blow, the kit's — today's two rules).
 */
export type DamageCause = BlowCause | 'star' | 'space' | 'wormhole' | 'planet' | 'wreck';

/**
 * 69.65 (DECISIONS 289; constants/explosions.ts BLAST_RADII): a person-family burst as one model, in world units — a person
 * by two radii from the nearest point of its body, a hull by kind, a structure over the kill radius (core/explosions.ts
 * burstModelFor builds it; core/rocket.ts passes it). Absent: the circle (the turn game's, and the action clock's before it).
 */
export interface BurstModel {
  kill: number; wound: number;
  /** a person within `kill` takes `heart`; a wound from `woundStart` at `kill` to nothing at `wound` */
  heart: number; woundStart: number;
  kind: 'he' | 'frag' | 'shaped';
  /** a shaped charge's contact: within it the class's penetration meets the plate; beyond, `blastPen` and `blastShare` of the heart over the wound radius */
  contact: number; blastPen: number; blastShare: number;
  /** a body's girth and its height by stance (standing, crouched, prone) */
  body: { radius: number; stand: number; crouch: number; prone: number };
}
/** 69.65: what a person `d` from the nearest of its body takes from `m` (before cover, armour and the friendly share). */
export function burstPersonDamage(m: BurstModel, d: number): number {
  if (d <= m.kill) return m.heart;
  return d >= m.wound ? 0 : m.woundStart * (m.wound - d) / (m.wound - m.kill);
}
/** 69.65: a body's nearest point's distance from `point` — its line from the feet up its stance's height, less its girth. */
export function burstBodyDistance(m: BurstModel, feet: Vec3, up: Vec3, stance: 'stand' | 'crouch' | 'prone' | undefined, point: Vec3): number {
  const h = stance === 'prone' ? m.body.prone : stance === 'crouch' ? m.body.crouch : m.body.stand;
  const rel = vecSub(point, feet), along = Math.max(0, Math.min(h, vecDot(rel, up)));
  return Math.max(0, vecLength(vecSub(rel, vecScale(up, along))) - m.body.radius);
}
/** 69.65: what of a burst's heart reaches a hull or a structure `d` from its shape, and the penetration it brings (`pen` the class's). */
export function burstHullReach(m: BurstModel, d: number, pen: number): { share: number; pen: number } {
  if (m.kind !== 'shaped') return { share: Math.max(0, 1 - d / Math.max(m.kill, 1e-9)), pen };
  if (d <= m.contact) return { share: 1, pen };
  return { share: Math.max(0, 1 - d / Math.max(m.wound, 1e-9)) * m.blastShare, pen: Math.min(pen, m.blastPen) };   // (its blast and spall, beyond the jet)
}

/** 63.4: the share of a blow of `cause` that a thing of `material` takes (the damage table; today's numbers are 1 or 0). */
export function blowShare(cause: BlowCause, material: Material): number {
  return DAMAGE_VS[cause][material];
}
/** What a structure is made of (its body: a hull for a gun or a vehicle, light for a building). */
export function structureMaterial(s: Pick<StructureState, 'type'>): Material {
  const def = BODIES[s.type];
  return def.material ?? (def.kind === 'vehicle' ? 'hull' : 'light');
}

/** The flat amount a unit's armor takes off an armored blow (the kit's; a body def's `armor` joins it when a vehicle has one). */
export function armorOf(unit: Pick<PlayerState, 'modifiers'>): number {
  return unit.modifiers?.armorReduction ?? 0;
}
/** What a blow of `amount` deals this unit after armor, by cause. */
export function armoredAmount(unit: Pick<PlayerState, 'modifiers'>, amount: number, cause: DamageCause): number {
  return cause === 'blast' || cause === 'burst' || cause === 'bullet' ? Math.max(0, amount - armorOf(unit)) : amount;
}

export interface UnitHit { unit: PlayerState; dealt: number; killed: boolean }

/**
 * Every write of a unit's health goes through here: armor by cause, the floor at 0, death at 0, and — when `by`
 * names the one who hurt it — the blame (`lastDamagedBy`, which kill credit for a launched unit reads). Pure; the
 * caller decides what to credit, log and report from `dealt` and `killed`.
 */
export function damageUnit(unit: PlayerState, amount: number, cause: BlowCause, by?: number): UnitHit {
  const dealt = armoredAmount(unit, amount, cause) * blowShare(cause, 'flesh');
  const health = Math.max(0, unit.health - dealt);
  const alive = health > 0;
  const hit: PlayerState = by === undefined ? { ...unit, health, alive } : { ...unit, health, alive, lastDamagedBy: by };
  return { unit: hit, dealt, killed: !alive };
}
/**
 * 63.4 step 7, decision 124 at the door: a seated unit whose hull a blast destroyed. `share` is the share of the blast
 * that reached it (0 = nothing; 1 = the whole blast at the hatch): the hatch rule (core/body.ts hatchSurvival) decides —
 * below the hatch line, or at or past the threshold, it dies; else it keeps its health scaled by what got through. Blamed
 * on `by` when it is hurt (the blast's rule: a scratch under a point is blamed on nobody).
 */
export function hatchOutcome(unit: PlayerState, seat: Pick<SeatDef, 'entry'>, share: number, by?: number): UnitHit {
  const r = hatchSurvival(seat, share, unit.health);
  const health = r.survives ? r.health : 0;
  const dealt = unit.health - health;
  const blamed = by !== undefined && (dealt > 1 || health <= 0);
  const hit: PlayerState = blamed ? { ...unit, health, alive: health > 0, lastDamagedBy: by } : { ...unit, health, alive: health > 0 };
  return { unit: hit, dealt, killed: health <= 0 };
}
/** A death that is not a blow: the star, deep space, a wormhole's limit, the planet under the unit going nova, the hull around it dying (124). */
export function killUnit(unit: PlayerState, _cause: Extract<DamageCause, 'star' | 'space' | 'wormhole' | 'planet' | 'wreck'>): PlayerState {
  return { ...unit, alive: false, health: 0 };
}
/** The shooter's book: damage dealt and kills. */
export function creditHit(shooter: PlayerState, dealt: number, kills: number): PlayerState {
  return { ...shooter, damageDealt: shooter.damageDealt + dealt, kills: shooter.kills + kills };
}
/** Every write of a structure's health: the table's share for its material, the floor at 0, destroyed at 0. */
export function damageStructure(structure: StructureState, amount: number, cause: BlowCause = 'blast'): { structure: StructureState; destroyed: boolean } {
  if (structure.healthFrom === 'modules') return { structure, destroyed: false };   // 72.3 step 6: its modules take the blow, and its health is theirs (core/collapse.ts)
  const health = Math.max(0, structure.health - amount * blowShare(cause, structureMaterial(structure)));
  return { structure: { ...structure, health }, destroyed: health <= 0 };
}
/** 63.4 step 5: every write of a terrain feature's health (a vent, a geyser, a crystal) — 63.5: by its ground's row (GROUND_DAMAGE). */
export function damageHazard(hazard: HazardState, amount: number, cause: BlowCause): { hazard: HazardState; destroyed: boolean } {
  const health = Math.max(0, hazard.health - amount * GROUND_DAMAGE[cause][HAZARD_GROUND[hazard.hazardType] ?? 'soil']);   // (a kind with no ground: soil's row)
  return { hazard: { ...hazard, health, alive: health > 0 }, destroyed: health <= 0 };
}
/** 63.4 step 5: every write of a planted flag's health (a flag without health cannot be hurt) — light in the table. */
export function damageFlag(flag: PlantedFlagState, amount: number, cause: BlowCause): { flag: PlantedFlagState; destroyed: boolean } {
  if (flag.health == null) return { flag, destroyed: false };
  const health = Math.max(0, flag.health - amount * blowShare(cause, 'light'));
  return { flag: { ...flag, health, alive: health > 0 }, destroyed: health <= 0 };
}
/** 63.4 step 5: every write of a crashed drop can's health — light in the table. */
export function damageDropCan(can: CrashedDropCanState, amount: number, cause: BlowCause): { can: CrashedDropCanState; destroyed: boolean } {
  const health = Math.max(0, can.health - amount * blowShare(cause, 'light'));
  return { can: { ...can, health }, destroyed: health <= 0 };
}
/**
 * 63.4 step 6: a prop takes a blow — the table's share for its material off its health (a tree's in `floraHealth`,
 * absent = whole), felled at 0 (`alive` false: its standing shape goes — a kit tree leaves its stump and its trunk lying
 * away from the blow, E5.2), and the blow logged
 * in `propEvents`. A rock cannot be destroyed (130 (b)). A prop belongs to no side: friendly fire never spares it.
 */
function damageProp(state: GameState, kind: 'flora' | 'rock', id: number, amount: number, cause: BlowCause, by?: number, from?: Vec3): { state: GameState; dealt: number; killed: boolean } {
  const def = PROPS[kind];
  const blow = amount * blowShare(cause, def.material);
  if (def.health === null || !(blow > 0) || kind !== 'flora') return { state, dealt: 0, killed: false };
  const tree = state.flora.find(f => f.id === id);
  if (!tree || !tree.alive) return { state, dealt: 0, killed: false };
  const before = state.floraHealth?.[id] ?? def.health;
  const health = Math.max(0, before - blow);
  const felled = health <= 0;
  const event: PropEvent = { tick: state.tick ?? state.turnNumber, cause, kind, id, ...(by !== undefined ? { by } : {}), amount: before - health, felled, ...(from ? { from: { x: from.x, y: from.y, z: from.z } } : {}) };
  const { [id]: _gone, ...rest } = state.floraHealth ?? {};
  const floraHealth = felled ? rest : { ...rest, [id]: health };
  // partial destruction step 3: a blow that steps the tree's form (it stands on) sheds the limbs the step lost — they
  // fall and lie as debris, and land as a light `drop` blow on whoever is under them (decision 141)
  const formFrom = treeForm(state, tree), formTo = felled ? formFrom : treeForm({ config: state.config, floraHealth }, tree);
  const shed = formTo > formFrom ? shedLimbs(state, tree, formFrom, formTo, from, event.tick, state.debrisSeq ?? 0) : [];
  if (formTo > formFrom) { event.form = formTo; event.debris = shed.map(d => d.id); }
  let next: GameState = {
    ...state,
    flora: felled ? state.flora.map(f => f.id === id ? { ...f, alive: false } : f) : state.flora,
    floraHealth: Object.keys(floraHealth).length ? floraHealth : undefined,
    propEvents: [...(state.propEvents ?? []), event],
    ...(shed.length ? { debris: settleDebris([...(state.debris ?? []), ...shed], state.planets), debrisSeq: (state.debrisSeq ?? 0) + shed.length } : {}),
  };
  for (const d of shed) for (const hit of debrisLandsOn(next, d)) next = applyDamage(next, { unit: hit.unitId }, hit.amount, 'drop').state;
  return { state: next, dealt: before - health, killed: felled };
}

/** Partial destruction: the form a prop's health puts it in (core/debris.ts, where the forms are read in play). */
export { propForm } from './debris';
import { layoutOfStructure } from './modules';
import { blastModules, undermineModules } from './moduleDamage';

/** What the door at state level can hit: a unit, a structure, a drone, a prop (63.4 step 5). */
export type DamageTarget = { unit: number } | { structure: number } | { drone: number } | { prop: { kind: 'flora' | 'rock'; id: number; from?: Vec3 } };

/**
 * The door at state level: one target, one blow, the shooter credited. The blast (`calculateDamage`) runs the
 * same unit and structure doors over its arrays and credits the shooter once for the whole.
 */
export function applyDamage(
  state: GameState, target: DamageTarget, amount: number, cause: BlowCause, by?: number,
): { state: GameState; dealt: number; killed: boolean } {
  if ('structure' in target) {
    const s = state.structures.find(x => x.id === target.structure);
    if (!s) return { state, dealt: 0, killed: false };
    const r = damageStructure(s, amount, cause);
    // 63.12 (vehicleZones): a vehicle a round destroys was shot to death — it burns out, its crew bailing out (core/vehicleZones.ts)
    const out = r.destroyed && cause === 'bullet' && zonesOn(state.config) && BODIES[s.type].kind === 'vehicle' ? { ...r.structure, gunfireKill: true as const } : r.structure;
    return { state: { ...state, structures: state.structures.map(x => x.id === s.id ? out : x) }, dealt: s.health - r.structure.health, killed: r.destroyed };
  }
  if ('drone' in target) {
    // a drone (I2): any blow that reaches it downs it, whatever its damage (a rocket round's is 0) — 65.42: but a bullet
    // only its DRONE_HITS-th (the owner: "2 hits to bring down")
    const d = (state.drones ?? []).find(x => x.id === target.drone);
    if (!d || !d.alive) return { state, dealt: 0, killed: false };
    if (cause === 'bullet' && (d.hits ?? 0) + 1 < DRONE_HITS) return { state: { ...state, drones: state.drones!.map(x => x.id === d.id ? { ...x, hits: (x.hits ?? 0) + 1 } : x) }, dealt: 0, killed: false };
    return { state: endDrone(state, d.id, 'shotDown'), dealt: 0, killed: true };
  }
  if ('prop' in target) return damageProp(state, target.prop.kind, target.prop.id, amount, cause, by, target.prop.from);
  const t = state.players.find(p => p.id === target.unit);
  if (!t) return { state, dealt: 0, killed: false };
  const hit = damageUnit(t, amount, cause, by);
  const players = state.players.map(p => p.id === t.id ? hit.unit : p.id === by ? creditHit(p, hit.dealt, hit.killed ? 1 : 0) : p);
  return { state: { ...state, players }, dealt: hit.dealt, killed: hit.killed };
}

/**
 * How far a blast of `blastRadius` reaches a structure on a planet of `planetRadius`: the damage circle plus the prop's
 * planet-scale width, or — for a person-scale structure (`halfWidth`, core/surfaceWalk.ts personScaleStructureHalfWidth) —
 * plus its own half width (Phase 63's rule: a prop's reach follows its own scale).
 */
export function structureBlastReach(blastRadius: number, planetRadius: number, halfWidth: number | null = null): number {
  return halfWidth === null ? blastRadius * 2.2 + propHitBonus(planetRadius, STRUCTURE_HALF_WIDTH) : blastRadius * 2.2 + halfWidth;
}

/**
 * The friendly-fire setting as a share of damage (0 = none, 1 = full) — decision 130 (d), "Everything follows the
 * setting": bullets, blasts and the burst read it here, so an unset setting means the same (none) on every path.
 */
export function friendlyShare(config: { friendlyFire?: number } | undefined): number {
  return (config?.friendlyFire ?? 0) / 100;
}

export function calculateDamage(
  players: PlayerState[],
  planets: PlanetState[],
  impactPoint: Vec3,
  weaponKey: WeaponKey,
  structures: StructureState[] = [],
  shooterTeamIndex?: number,
  friendlyFireScale?: number,
  hazards: HazardState[] = [],
  knockbackMultiplier: number = 1.0,
  shooterId?: number,
  plantedFlags: PlantedFlagState[] = [],
  crashedDropCans: CrashedDropCanState[] = [],
  /** The shooter's kit may scale damage / blast radius (core/unitModifiers.ts). */
  weaponOverride?: { damage?: number; blastRadius?: number },
  /**
   * 62.8 (SCALE.md §6, launch and landing): the struck are people of this k — the push is converted into the person
   * family (its velocity, lift and slide × k; the launch test on the unconverted strength). 1 = today's units.
   */
  personScale = 1,
  /**
   * 63.4 step 7 (the clock policy's `blastReach: 'shape'`, core/blastReach.ts): how far the blast is from each structure's
   * shape and whether something between stops it — a structure is then reached over the blast's damage circle from its
   * shape (one with no shape, from its centre), and a gun this blast destroys under its gunner settles the gunner by
   * decision 124's hatch rule. Absent: the turn game's centre rule (structureBlastReach) and no hatch rule.
   */
  structureReach?: (s: StructureState) => StructureReach | null,
  /** X18 step 2: what a shield installation scales this unit's blast by (core/installations.ts shieldFactorFor); absent or 1 = none. */
  shieldFactorFor?: (player: PlayerState) => number,
  /** 63.4 step 6: a shell's blast, or a person-scale burst (a rocket's) — the damage table's row. */
  cause: 'blast' | 'burst' = 'blast',
  /** 63.4 step 7: people the blast cannot reach (a burst's covered, core/rocket.ts) — untouched, but still counted for their side. */
  spared?: ReadonlySet<number>,
  /** §5.21 step 6: the burst's class push (core/explosions.ts explosionPush) — its circle, and its strength at the heart; absent: today's (3 R, by R). */
  push?: { radius: number; strength: number },
  /** 63.12 (vehicleZones): the burst's penetration and the state its vehicles stand in — each zoned vehicle it reaches met in the zone nearest it; absent: today's rule. */
  zones?: BurstZones,
  /** 69.65: the burst as one model (two radii for people, hulls by kind, structures over the kill radius); absent: the circle */
  model?: BurstModel,
  /** 69.47 step 1 (with GameConfig.charges): the share of this blast a structure takes — a toughened story target's of a squad's burst (core/charges.ts toughShareFor); absent: whole. */
  structureShare?: (s: StructureState) => number,
): DamageResult {
  const weapon = weaponOverride
    ? { ...WEAPONS[weaponKey], damage: weaponOverride.damage ?? WEAPONS[weaponKey].damage, blastRadius: weaponOverride.blastRadius ?? WEAPONS[weaponKey].blastRadius }
    : WEAPONS[weaponKey];
  const kills: number[] = [];
  const damages: Array<{ playerId: number; damage: number }> = [];
  const knockbacks: Array<{ playerId: number; launched: boolean; sheltered?: boolean }> = [];
  const destroyedStructures: number[] = [];
  const destroyedHazards: number[] = [];
  const destroyedFlags: number[] = [];
  const destroyedDropCans: number[] = [];
  const ffScale = friendlyFireScale ?? 1.0; // default: full damage to all
  // 130 (d), the owner: "Everything follows the setting" — the shooter's own side's structures, flags and drop cans take
  // the friendly-fire share of a blast, as its people do (with it off, none)
  const ownSide = (team: number | undefined) => shooterTeamIndex !== undefined && team === shooterTeamIndex ? ffScale : 1.0;
  const teamOf = new Map(players.map(p => [p.id, p.teamIndex]));

  // 63.12: each zoned vehicle the burst meets — the zone nearest it, and whether it gets through (kept per vehicle)
  const zoneHits = new Map<number, { table: NonNullable<ReturnType<typeof zoneTableOf>>; hit: NonNullable<ReturnType<typeof zoneOf>>; penetrates: boolean; share: number } | null>();
  // 69.65: a vehicle's own reach under the model (its shape's distance: a shaped charge brings its penetration on contact only)
  // (DECISIONS 300's cost: each structure's reach once a blast — the structure loop and modelReach asked it twice, its cover test each time)
  const reachMemo = new Map<number, StructureReach | null>();
  const reachOf = (v: StructureState): StructureReach | null => {
    if (!structureReach) return null;
    if (!reachMemo.has(v.id)) reachMemo.set(v.id, structureReach(v));
    return reachMemo.get(v.id)!;
  };
  const modelReach = (v: StructureState) => {
    if (!model) return null;
    const r = reachOf(v);
    return burstHullReach(model, r ? r.dist : vecLength(vecSub(getStructureWorldPosition(v, planets), impactPoint)), zones?.pen ?? 0);
  };
  const zoneHit = (id: number) => {
    if (!zones) return null;
    if (zoneHits.has(id)) return zoneHits.get(id)!;
    const v = structures.find(x => x.id === id), table = v ? zoneTableOf(v) : null, hit = v && table ? zoneOf(zones.state, v, impactPoint, true) : null;
    const pen = v && model ? modelReach(v)!.pen : zones.pen;
    const out = table && hit ? { table, hit, penetrates: pen >= hit.effective, share: hullShare(table.cls, pen, hit.effective) } : null;   // (69.29 step 4: at its angle; the hull's wear)
    zoneHits.set(id, out);
    return out;
  };

  // Structure damage (first: a gun this blast destroys settles its gunner below)
  const wrecked = new Map<number, StructureState>();   // 63.4 step 7: the seated bodies this blast destroys
  const newStructures = structures.map(structure => {
    if (structure.health <= 0) return structure;

    // 63.4 step 7: by its shape under the clock policy's `blastReach: 'shape'` (a burst's covered structure is spared);
    // else from its centre — bigger buildings on bigger worlds easier to hit (zero bonus at the reference radius)
    const reach = reachOf(structure);
    if (reach?.covered) return structure;
    const dist = reach ? reach.dist : vecLength(vecSub(getStructureWorldPosition(structure, planets), impactPoint));
    // (63.1d's follow-up: a moving vehicle's hull feels a shell over a person-scale share of its blast radius, VEHICLE_BLAST)
    const body = BODIES[structure.type as BodyKey];
    const hull = cause === 'blast' && body?.kind === 'vehicle' && body.mobility !== 'fixed';   // (one that moves: the gun pit keeps its circle)
    const effectiveRadius = model ? model.kill : hull ? weapon.blastRadius * VEHICLE_BLAST.reachShare : structureReach ? weapon.blastRadius * 2.2 : structureBlastReach(weapon.blastRadius, planets[structure.planetIndex].radius, null);
    // 69.65: by the burst's kind — HE over its kill radius, a shaped charge whole on contact and its blast beyond
    const modelShare = model ? modelReach(structure)!.share : null;

    if (modelShare !== null ? modelShare > 0 : dist < effectiveRadius) {
      const factor = modelShare ?? Math.max(0, 1 - dist / effectiveRadius);
      const side = ownSide(teamOf.get(structure.ownerId));
      const zh = zoneHit(structure.id);   // 63.12: a burst its zone stops dents the plates only (69.29 step 4: an armoured or heavy hull worn by its margin)
      const tough = structureShare ? structureShare(structure) : 1;   // 69.47 step 1: a toughened target's share of a squad's burst
      const dmg = weapon.damage * factor * side * tough * (zh ? zh.share : 1);
      if (dmg <= 0) return structure;
      const r = damageStructure(structure, dmg, cause);
      if (r.destroyed) { destroyedStructures.push(structure.id); if (structureReach && structure.seats?.length) wrecked.set(structure.id, structure); }
      // 69.29 step 4 (DECISIONS 245 (3)): through its running gear or its engine, a mobility kill
      const knocked = zh?.penetrates ? knockedOut(zh.hit.zone, zh.hit.local) : null;
      // partial destruction step 4: the marked parts take the blast by their own distance, out of their own health
      let out = knocked && !r.destroyed ? { ...r.structure, disabled: { ...(r.structure.disabled ?? {}), ...knocked } } : r.structure;
      if (reach?.parts && !r.destroyed) {
        const share = blowShare(cause, structureMaterial(structure));
        const parts = blastParts(structure, reach.parts, d => weapon.damage * Math.max(0, 1 - d / effectiveRadius) * side * share * tough);
        if (parts) out = { ...out, parts };
      }
      // 63.14 step 4: a modular building's modules take the blast by their own distance, each through the table for its material
      // (72.3: the distances are people, the radius world units — a person is PERSON_BODY_HEIGHT × the struck's k)
      if (reach?.modules && !r.destroyed) {
        const layout = layoutOfStructure(structure), person = PERSON_BODY_HEIGHT * personScale;
        if (layout) out = blastModules(out, layout, reach.modules, d => weapon.damage * Math.max(0, 1 - d * person / effectiveRadius) * side * tough, cause, reach.tick ?? 0);
      }
      return out;
    }

    return structure;
  });

  const newPlayers = players.map(player => {
    if (!player.alive) return player;
    if (player.airborne) return player; // Airborne players not affected by ground explosions
    if (player.boarded) return player;   // (208: aboard the wave ship, untouched)
    // 63.12: seated in a zoned vehicle the burst met — the zones decide, not the hull's cover: through the zone the seat sits
    // behind it kills outright; stopped by the hull it spares those inside (one standing out of a hatch takes it as anyone);
    // through elsewhere, the zone's share of it
    const zh = player.mount && !player.seatPos ? zoneHit(player.mount.bodyId) : null;
    const zoneSeat = zh ? burstOnSeat(zh.table, zh.hit, zh.penetrates, player.mount!.seat) : null;
    if (!zoneSeat && spared?.has(player.id)) return player;   // 63.4 step 7: out of the blast's reach (a burst's covered)

    const playerPos = getPlayerWorldPosition(player, planets);
    const dist = vecLength(vecSub(playerPos, impactPoint));
    const effectiveRadius = weapon.blastRadius * 2.2;
    // 69.65: by two radii from the body's nearest point — its share of the class's heart (the hatch rule's) and its damage
    const near = model ? burstBodyDistance(model, playerPos, vecNormalize(vecSub(playerPos, planets[player.planetIndex].position)), bodyStanceOf(player), impactPoint) : 0;
    const blow = model ? burstPersonDamage(model, near) : 0;
    const knockbackRadius = push ? push.radius : weapon.blastRadius * KNOCKBACK_RADIUS_FACTOR; // Knockback extends further than damage (§5.21 step 6: or the class's own)

    // Friendly fire scaling: reduce damage/knockback for teammates
    const isFriendly = shooterTeamIndex !== undefined && player.teamIndex === shooterTeamIndex;
    const damageMultiplier = isFriendly ? ffScale : 1.0;

    let updatedPlayer = { ...player };

    // Cover (37.18): the rim between this unit and the impact shelters it — knockback always, blast by the dial
    const rim = dist < knockbackRadius ? rimBetween(planets[player.planetIndex], player.surfaceDir, impactPoint) : 0;
    const sheltered = rim > 0.05;

    if (zoneSeat?.kind === 'outright') {
      damages.push({ playerId: player.id, damage: player.health });
      kills.push(player.id);
      return { ...player, health: 0, alive: false, ...(shooterId !== undefined ? { lastDamagedBy: shooterId } : {}) };
    }

    // 63.4 step 7, decision 124: the blast destroyed the gun under this gunner — its outcome is the hatch rule alone, in
    // place of its own blast damage: what reached it (the blast's share at the gunner) at or past the threshold kills it
    // with the gun; below, it bails out with its health scaled by what got through (reconcileSeats unseats it)
    const hull = player.mount ? wrecked.get(player.mount.bodyId) : undefined;
    if (hull) {
      const reached = model ? Math.min(1, blow / model.woundStart) : dist < effectiveRadius ? Math.max(0, 1 - dist / effectiveRadius) : 0;
      const share = reached * damageMultiplier * coverDamageFactor(rim) * (shieldFactorFor ? shieldFactorFor(player) : 1);
      const hit = hatchOutcome(player, BODIES[hull.type].seats[player.mount!.seat], share, shooterId);
      if (hit.dealt > 1) damages.push({ playerId: player.id, damage: hit.dealt });
      if (hit.killed) kills.push(player.id);
      return hit.unit;   // no push: a seated unit is anchored
    }

    if (zoneSeat?.kind === 'spared') return player;   // 63.12: the hull stopped it (no push: a seated unit is anchored)

    // Apply damage
    if (model ? blow > 0 : dist < effectiveRadius) {
      const unshielded = (model ? blow : weapon.damage * Math.max(0, 1 - dist / effectiveRadius)) * damageMultiplier * coverDamageFactor(rim) * (zoneSeat?.kind === 'share' ? zoneSeat.share : 1);
      const shield = shieldFactorFor ? shieldFactorFor(player) : 1;   // X18 step 2: a shield installation nearby
      let raw = shield === 1 ? unshielded : unshielded * shield;
      // 185: a blast meets the armour — the vest its blast share, the helmet its share of the head's part, out of their lives
      // (worn by what they stop); 65.35 step 8 (the owner: "sure vest interacts with explosions"): a shell's too, not a burst's only
      if (updatedPlayer.armour) { const t = throughArmour(updatedPlayer, 'blast', undefined, raw); raw = t.amount; updatedPlayer = t.unit; }
      const dmg = armoredAmount(player, raw, cause) * blowShare(cause, 'flesh');
      // a scratch under a point is reported to nobody and blamed on nobody (the blast's rule)
      if (dmg > 1) damages.push({ playerId: player.id, damage: dmg });
      const hit = damageUnit(updatedPlayer, raw, cause, dmg > 1 ? shooterId : undefined);
      if (hit.killed) kills.push(player.id);
      updatedPlayer = hit.unit;
    }

    // Apply knockback (only to living players, scaled for friendly fire). A seated unit is anchored by its gun: it
    // takes the damage but is not launched (a launched gunner stayed marked as mounted — core/seats.ts)
    if (dist < knockbackRadius && updatedPlayer.alive && !player.mount) {
      const knockbackResult = calculateKnockback(
        player,
        planets,
        playerPos,
        impactPoint,
        dist,
        knockbackRadius,
        (push ? (2 * push.strength) / KNOCKBACK_FORCE : weapon.blastRadius) * damageMultiplier,   // (a class's push: its strength at the heart)
        knockbackMultiplier * coverKnockbackFactor(rim),
        personScale,
      );

      if (knockbackResult) {
        knockbacks.push({ playerId: player.id, launched: knockbackResult.launched, ...(sheltered ? { sheltered: true } : {}) });
        updatedPlayer = {
          ...updatedPlayer,
          ...knockbackResult.playerUpdate,
          ...(shooterId !== undefined ? { lastDamagedBy: shooterId } : {}),
        };
      }
    }

    return updatedPlayer;
  });


  // Hazard damage
  const newHazards = hazards.map(hazard => {
    if (!hazard.alive) return hazard;

    const hazardPos = getHazardWorldPosition(hazard, planets);
    const dist = vecLength(vecSub(hazardPos, impactPoint));
    const effectiveRadius = weapon.blastRadius * 2.2 + propHitBonus(planets[hazard.planetIndex].radius, HAZARD_HALF_WIDTH);

    if (dist < effectiveRadius) {
      const factor = Math.max(0, 1 - dist / effectiveRadius);
      const r = damageHazard(hazard, weapon.damage * factor, cause);
      if (r.destroyed) destroyedHazards.push(hazard.id);
      return r.hazard;
    }

    return hazard;
  });

  // Flag damage
  const newFlags = plantedFlags.map(flag => {
    if (flag.alive === false) return flag;
    if (flag.health == null) return flag; // indestructible

    const flagPos = getFlagWorldPosition(flag, planets);
    const dist = vecLength(vecSub(flagPos, impactPoint));
    const effectiveRadius = weapon.blastRadius * 2.2;

    if (dist < effectiveRadius) {
      const factor = Math.max(0, 1 - dist / effectiveRadius);
      const dmg = weapon.damage * factor * ownSide(flag.teamIndex);
      if (dmg <= 0) return flag;
      const r = damageFlag(flag, dmg, cause);
      if (r.destroyed) destroyedFlags.push(flag.id);
      return r.flag;
    }

    return flag;
  });

  // Drop can damage
  const newDropCans = crashedDropCans.map(can => {
    if (can.health <= 0) return can;

    const canPos = getDropCanWorldPosition(can, planets);
    const dist = vecLength(vecSub(canPos, impactPoint));
    const effectiveRadius = weapon.blastRadius * 2.2;

    if (dist < effectiveRadius) {
      const factor = Math.max(0, 1 - dist / effectiveRadius);
      const dmg = weapon.damage * factor * ownSide(can.teamIndex);
      if (dmg <= 0) return can;
      const r = damageDropCan(can, dmg, cause);
      if (r.destroyed) destroyedDropCans.push(can.id);
      return r.can;
    }

    return can;
  });

  return { players: newPlayers, kills, damages, knockbacks, structures: newStructures, destroyedStructures, hazards: newHazards, destroyedHazards, plantedFlags: newFlags, destroyedFlags, crashedDropCans: newDropCans, destroyedDropCans };
}

// =============================================================================
// KNOCKBACK CALCULATION
// =============================================================================

interface KnockbackResult {
  launched: boolean;
  playerUpdate: Partial<PlayerState>;
}

function calculateKnockback(
  player: PlayerState,
  planets: PlanetState[],
  playerPos: Vec3,
  impactPoint: Vec3,
  dist: number,
  knockbackRadius: number,
  blastRadius: number,
  knockbackMultiplier: number = 1.0,
  personScale = 1,
): KnockbackResult | null {
  const planet = planets[player.planetIndex];
  const surfaceNormal = vecNormalize(player.surfaceDir);

  // Direction away from impact (raw, before upward bias)
  const rawKnockDir = vecNormalize(vecSub(playerPos, impactPoint));

  // Apply upward bias: blend between raw direction and surface normal (up)
  // This makes explosions at feet send players skyward rather than just sideways
  const biasedDir = vecNormalize(vecAdd(
    vecScale(rawKnockDir, 1 - KNOCKBACK_UPWARD_BIAS),
    vecScale(surfaceNormal, KNOCKBACK_UPWARD_BIAS)
  ));

  // Knockback strength falls off with distance
  const strength = dpow(1 - dist / knockbackRadius, KNOCKBACK_FALLOFF) * KNOCKBACK_FORCE * (blastRadius / 2) * knockbackMultiplier;

  if (strength < 0.5) return null; // Too weak to matter

  // Calculate knockback velocity with upward-biased direction (a person's, × k: the same body lengths under g × k)
  const knockVel = vecScale(biasedDir, personScale === 1 ? strength : strength * personScale);

  // Any significant knockback launches the player airborne for visual feedback
  // Even if they'll land back on the same planet, they should fly through the air
  if (strength >= KNOCKBACK_MIN_LAUNCH_STRENGTH) {
    // Player is launched! May land back on same planet or escape to another
    return {
      launched: true,
      playerUpdate: {
        airborne: true,
        airbornePos: vecAdd(playerPos, vecScale(surfaceNormal, personScale === 1 ? 0.5 : 0.5 * personScale)), // Slightly above surface
        airborneVel: knockVel,
        airborneCause: 'knockback' as const,
      },
    };
  }

  // Very weak knockback - just slide along surface
  const tangentVel = vecSub(knockVel, vecScale(surfaceNormal, vecDot(knockVel, surfaceNormal)));
  const slideDistance = vecLength(tangentVel) * 0.3;

  if (slideDistance > (personScale === 1 ? 0.2 : 0.2 * personScale)) {
    const tangentDir = vecNormalize(tangentVel);
    const newSurfaceDir = vecNormalize(vecAdd(player.surfaceDir, vecScale(tangentDir, slideDistance / planet.radius)));

    return {
      launched: false,
      playerUpdate: {
        surfaceDir: newSurfaceDir,
      },
    };
  }

  return null;
}

// =============================================================================
// CRATER CREATION
// =============================================================================

export interface CraterResult {
  planet: PlanetState;
  modifiedVertices: number[];
  newColors: Float32Array;
  /**
   * The vertices whose colours (with their facets) may differ from the colours it was given to recolour — the fog's
   * changed vertices are found among them, not over the whole planet (the landing tick). Absent when its colours were
   * computed whole.
   */
  recoloured?: number[];
}

/** A crater's depth at its centre, as a share of its radius (× the depth multiplier). The kernel's crater (lib.rs) uses the same. */
export const CRATER_DEPTH_FACTOR = 0.55;

export function createCrater(
  planet: PlanetState,
  impactWorld: Vec3,
  blastRadius: number,
  depthMultiplier: number = 1.0,
  prevColors?: Float32Array,   // current vertex colours → only the touched facets are recoloured
): CraterResult {
  // Indestructible bodies (stars) cannot be cratered
  if (planet.indestructible) {
    return { planet, modifiedVertices: [], newColors: new Float32Array(0) };
  }

  // Convert impact point to local coordinates
  const localImpact = vecSub(impactWorld, planet.position);
  const impactDir = vecNormalize(localImpact);
  const angularRadius = blastRadius / planet.radius;

  const newHeightmap = new Float32Array(planet.heightmap);
  const modifiedVertices: number[] = [];

  // (the vertices within the blast, ascending — the whole-planet scan's order and arithmetic: core/vertexCells.ts)
  const near = verticesNear(planet.basePositions, impactDir, angularRadius), count = near ? near.length : planet.heightmap.length;
  for (let k = 0; k < count; k++) {
    const i = near ? near[k] : k;
    const bx = planet.basePositions[i * 3];
    const by = planet.basePositions[i * 3 + 1];
    const bz = planet.basePositions[i * 3 + 2];
    const len = Math.sqrt(bx * bx + by * by + bz * bz);
    const dot = (bx * impactDir.x + by * impactDir.y + bz * impactDir.z) / len;
    const angle = dacos(Math.min(1, Math.max(-1, dot)));
    if (angle < angularRadius) {
      const craterT = angle / angularRadius;
      const depth = (1 - craterT * craterT) * blastRadius * CRATER_DEPTH_FACTOR * depthMultiplier;
      // Floor: never dig past the depth envelope (keeps surface radius > 0)
      newHeightmap[i] = Math.max(planet.heightmap[i] - depth, -planet.radius * MAX_CRATER_DEPTH_FRACTION);
      modifiedVertices.push(i);
    }
  }

  // Calculate mass loss from crater
  const volumeLoss = blastRadius * blastRadius * blastRadius * 0.1;
  const newMass = Math.max(planet.mass - volumeLoss * planet.density, planet.initialMass * 0.1);

  const newPlanet: PlanetState = {
    ...planet, heightmap: newHeightmap, mass: newMass,
    initialHeightmap: planet.initialHeightmap ?? planet.heightmap,   // pin the generated surface the first time
    scorch: accumulateScorch(planet, impactWorld, blastRadius, 'crater'),
  };
  const newColors = prevColors ? recolorVertices(newPlanet, prevColors, modifiedVertices) : computePlanetVertexColors(newPlanet);
  return { planet: newPlanet, modifiedVertices, newColors, ...(prevColors ? { recoloured: modifiedVertices } : {}) };
}

// =============================================================================
// TERRAIN DROP DAMAGE
// =============================================================================

export interface TerrainDropResult {
  structures: StructureState[];
  hazards: HazardState[];
  destroyedStructures: number[];
  destroyedHazards: number[];
  droppedStructures: number[];
  droppedHazards: number[];
  droppedRuinIds: number[];
  plantedFlags: PlantedFlagState[];
  destroyedFlags: number[];
  droppedFlags: number[];
  crashedDropCans: CrashedDropCanState[];
  destroyedDropCans: number[];
  droppedDropCans: number[];
  droppedFloraIds: number[];
  droppedCollectibleIds: number[];
  droppedCarryableIds: number[];
  droppedGroundItemIds: number[];
}

const TERRAIN_DROP_THRESHOLD = 0.05;

export function applyTerrainDropDamage(
  structures: StructureState[],
  hazards: HazardState[],
  ruins: RuinState[],
  oldPlanet: PlanetState,
  newPlanet: PlanetState,
  planetIndex: number,
  plantedFlags: PlantedFlagState[] = [],
  crashedDropCans: CrashedDropCanState[] = [],
  flora: FloraState[] = [],
  collectibles: CollectibleNode[] = [],
  carryables: CarryableObjectState[] = [],
  groundItems: GroundItemState[] = [],
  /** 72.3: a building whose health is its modules' (`healthFrom`) is undermined module by module (the config, the tick). */
  modular?: { config: GameState['config']; tick: number }
): TerrainDropResult {
  const destroyedStructures: number[] = [];
  const destroyedHazards: number[] = [];
  const droppedStructures: number[] = [];
  const droppedHazards: number[] = [];
  const droppedRuinIds: number[] = [];
  const destroyedFlags: number[] = [];
  const droppedFlags: number[] = [];
  const destroyedDropCans: number[] = [];
  const droppedDropCans: number[] = [];
  const droppedFloraIds: number[] = [];
  const droppedCollectibleIds: number[] = [];
  const droppedCarryableIds: number[] = [];
  const droppedGroundItemIds: number[] = [];

  const newStructures = structures.map(structure => {
    if (structure.health <= 0) return structure;
    if (structure.planetIndex !== planetIndex) return structure;
    // 72.3 (§5.35 (b)): a building whose health is its modules' loses its ground-floor bearings where the ground fell under them
    // (and where the whole would have taken the drop's blow, its ground floor takes it: the same trigger, the same amount)
    if (structure.healthFrom === 'modules' && modular) {
      const layout = layoutOfStructure(structure);
      if (!layout) return structure;
      const up = vecNormalize(structure.surfaceDir), sunk = getSurfaceRadius(oldPlanet, up) - getSurfaceRadius(newPlanet, up);
      if (sunk > TERRAIN_DROP_THRESHOLD) droppedStructures.push(structure.id);
      return undermineModules(structure, layout, oldPlanet, newPlanet, modular, modular.tick, sunk > TERRAIN_DROP_THRESHOLD ? sunk * FALL_DAMAGE_FACTOR * 10 : 0);
    }

    const oldRadius = getSurfaceRadius(oldPlanet, structure.surfaceDir);
    const newRadius = getSurfaceRadius(newPlanet, structure.surfaceDir);
    const heightDrop = oldRadius - newRadius;

    if (heightDrop <= TERRAIN_DROP_THRESHOLD) return structure;

    droppedStructures.push(structure.id);
    const fallDamage = heightDrop * FALL_DAMAGE_FACTOR * 10;
    const r = damageStructure(structure, fallDamage, 'drop');
    if (r.destroyed) destroyedStructures.push(structure.id);
    return r.structure;
  });

  const newHazards = hazards.map(hazard => {
    if (!hazard.alive) return hazard;
    if (hazard.planetIndex !== planetIndex) return hazard;

    const oldRadius = getSurfaceRadius(oldPlanet, hazard.surfaceDir);
    const newRadius = getSurfaceRadius(newPlanet, hazard.surfaceDir);
    const heightDrop = oldRadius - newRadius;

    if (heightDrop <= TERRAIN_DROP_THRESHOLD) return hazard;

    droppedHazards.push(hazard.id);
    const r = damageHazard(hazard, heightDrop * FALL_DAMAGE_FACTOR * 10, 'drop');
    if (r.destroyed) destroyedHazards.push(hazard.id);
    return r.hazard;
  });

  for (const ruin of ruins) {
    if (ruin.planetIndex !== planetIndex) continue;

    const oldRadius = getSurfaceRadius(oldPlanet, ruin.surfaceDir);
    const newRadius = getSurfaceRadius(newPlanet, ruin.surfaceDir);
    const heightDrop = oldRadius - newRadius;

    if (heightDrop > TERRAIN_DROP_THRESHOLD) {
      droppedRuinIds.push(ruin.id);
    }
  }

  // Flag terrain drop damage
  const newFlags = plantedFlags.map(flag => {
    if (flag.alive === false) return flag;
    if (flag.health == null) return flag; // indestructible
    if (flag.planetIndex !== planetIndex) return flag;

    const oldRadius = getSurfaceRadius(oldPlanet, flag.surfaceDir);
    const newRadius = getSurfaceRadius(newPlanet, flag.surfaceDir);
    const heightDrop = oldRadius - newRadius;

    if (heightDrop <= TERRAIN_DROP_THRESHOLD) return flag;

    droppedFlags.push(flag.id);
    const r = damageFlag(flag, heightDrop * FALL_DAMAGE_FACTOR * 10, 'drop');
    if (r.destroyed) destroyedFlags.push(flag.id);
    return r.flag;
  });

  // Drop can terrain drop damage
  const newDropCans = crashedDropCans.map(can => {
    if (can.health <= 0) return can;
    if (can.planetIndex !== planetIndex) return can;

    const oldRadius = getSurfaceRadius(oldPlanet, can.surfaceDir);
    const newRadius = getSurfaceRadius(newPlanet, can.surfaceDir);
    const heightDrop = oldRadius - newRadius;

    if (heightDrop <= TERRAIN_DROP_THRESHOLD) return can;

    droppedDropCans.push(can.id);
    const r = damageDropCan(can, heightDrop * FALL_DAMAGE_FACTOR * 10, 'drop');
    if (r.destroyed) destroyedDropCans.push(can.id);
    return r.can;
  });

  // Flora repositioning (no damage, like ruins)
  for (const f of flora) {
    if (!f.alive) continue;
    if (f.planetIndex !== planetIndex) continue;

    const oldRadius = getSurfaceRadius(oldPlanet, f.surfaceDir);
    const newRadius = getSurfaceRadius(newPlanet, f.surfaceDir);
    const heightDrop = oldRadius - newRadius;

    if (heightDrop > TERRAIN_DROP_THRESHOLD) {
      droppedFloraIds.push(f.id);
    }
  }

  // Collectible repositioning (no damage, like ruins)
  for (const c of collectibles) {
    if (c.collected) continue;
    if (c.planetIndex !== planetIndex) continue;

    const oldRadius = getSurfaceRadius(oldPlanet, c.surfaceDir);
    const newRadius = getSurfaceRadius(newPlanet, c.surfaceDir);
    const heightDrop = oldRadius - newRadius;

    if (heightDrop > TERRAIN_DROP_THRESHOLD) {
      droppedCollectibleIds.push(c.id);
    }
  }

  // Carryable repositioning (no damage, like ruins)
  for (const ca of carryables) {
    if (!ca.alive) continue;
    if (ca.carriedBy !== null) continue;
    if (ca.planetIndex !== planetIndex) continue;

    const oldRadius = getSurfaceRadius(oldPlanet, ca.surfaceDir);
    const newRadius = getSurfaceRadius(newPlanet, ca.surfaceDir);
    const heightDrop = oldRadius - newRadius;

    if (heightDrop > TERRAIN_DROP_THRESHOLD) {
      droppedCarryableIds.push(ca.id);
    }
  }

  // Ground item repositioning (no damage, like ruins)
  for (const gi of groundItems) {
    if (!gi.alive) continue;
    if (gi.planetIndex !== planetIndex) continue;

    const oldRadius = getSurfaceRadius(oldPlanet, gi.surfaceDir);
    const newRadius = getSurfaceRadius(newPlanet, gi.surfaceDir);
    const heightDrop = oldRadius - newRadius;

    if (heightDrop > TERRAIN_DROP_THRESHOLD) {
      droppedGroundItemIds.push(gi.id);
    }
  }

  return {
    structures: newStructures,
    hazards: newHazards,
    destroyedStructures,
    destroyedHazards,
    droppedStructures,
    droppedHazards,
    droppedRuinIds,
    plantedFlags: newFlags,
    destroyedFlags,
    droppedFlags,
    crashedDropCans: newDropCans,
    destroyedDropCans,
    droppedDropCans,
    droppedFloraIds,
    droppedCollectibleIds,
    droppedCarryableIds,
    droppedGroundItemIds,
  };
}

// =============================================================================
// HELPERS
// =============================================================================

function getHazardWorldPosition(hazard: HazardState, planets: PlanetState[]): Vec3 {
  const planet = planets[hazard.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, hazard.surfaceDir);
  const normal = vecNormalize(hazard.surfaceDir);
  return {
    x: planet.position.x + normal.x * surfaceRadius,
    y: planet.position.y + normal.y * surfaceRadius,
    z: planet.position.z + normal.z * surfaceRadius,
  };
}

function getFlagWorldPosition(flag: PlantedFlagState, planets: PlanetState[]): Vec3 {
  const planet = planets[flag.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, flag.surfaceDir);
  const normal = vecNormalize(flag.surfaceDir);
  return {
    x: planet.position.x + normal.x * surfaceRadius,
    y: planet.position.y + normal.y * surfaceRadius,
    z: planet.position.z + normal.z * surfaceRadius,
  };
}

function getStructureWorldPosition(structure: StructureState, planets: PlanetState[]): Vec3 {
  const planet = planets[structure.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, structure.surfaceDir) + (structure.baseLift ?? 0);   // 67.52: a build on its spot's fine ground
  const normal = vecNormalize(structure.surfaceDir);
  return {
    x: planet.position.x + normal.x * surfaceRadius,
    y: planet.position.y + normal.y * surfaceRadius,
    z: planet.position.z + normal.z * surfaceRadius,
  };
}

function getDropCanWorldPosition(can: CrashedDropCanState, planets: PlanetState[]): Vec3 {
  const planet = planets[can.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, can.surfaceDir);
  const normal = vecNormalize(can.surfaceDir);
  return {
    x: planet.position.x + normal.x * surfaceRadius,
    y: planet.position.y + normal.y * surfaceRadius,
    z: planet.position.z + normal.z * surfaceRadius,
  };
}

function getPlayerWorldPosition(player: PlayerState, planets: PlanetState[]): Vec3 {
  const planet = planets[player.planetIndex];
  const surfaceRadius = getSurfaceRadius(planet, player.surfaceDir);
  const normal = vecNormalize(player.surfaceDir);
  return {
    x: planet.position.x + normal.x * surfaceRadius,
    y: planet.position.y + normal.y * surfaceRadius,
    z: planet.position.z + normal.z * surfaceRadius,
  };
}



// =============================================================================
// TERRAFORMER — MOUND CREATION (reverse crater)
// =============================================================================

export function createMound(
  planet: PlanetState,
  impactWorld: Vec3,
  blastRadius: number,
  prevColors?: Float32Array,
): CraterResult {
  if (planet.indestructible) {
    return { planet, modifiedVertices: [], newColors: new Float32Array(0) };
  }

  const localImpact = vecSub(impactWorld, planet.position);
  const impactDir = vecNormalize(localImpact);
  const angularRadius = blastRadius / planet.radius;

  const newHeightmap = new Float32Array(planet.heightmap);
  const modifiedVertices: number[] = [];

  // (the vertices within the blast, ascending — the whole-planet scan's order and arithmetic: core/vertexCells.ts)
  const near = verticesNear(planet.basePositions, impactDir, angularRadius), count = near ? near.length : planet.heightmap.length;
  for (let k = 0; k < count; k++) {
    const i = near ? near[k] : k;
    const bx = planet.basePositions[i * 3];
    const by = planet.basePositions[i * 3 + 1];
    const bz = planet.basePositions[i * 3 + 2];
    const len = Math.sqrt(bx * bx + by * by + bz * bz);
    const dot = (bx * impactDir.x + by * impactDir.y + bz * impactDir.z) / len;
    const angle = dacos(Math.min(1, Math.max(-1, dot)));
    if (angle < angularRadius) {
      const craterT = angle / angularRadius;
      const height = (1 - craterT * craterT) * blastRadius * 0.4;
      // Cap: mounds stay inside the collision bounding shell
      newHeightmap[i] = Math.min(planet.heightmap[i] + height, planet.radius * MAX_TERRAIN_HEIGHT_FRACTION);
      modifiedVertices.push(i);
    }
  }

  // Add mass (reverse of crater mass loss)
  const volumeGain = blastRadius * blastRadius * blastRadius * 0.05;
  const newMass = planet.mass + volumeGain * planet.density;

  const newPlanet: PlanetState = {
    ...planet, heightmap: newHeightmap, mass: newMass,
    initialHeightmap: planet.initialHeightmap ?? planet.heightmap,
    scorch: accumulateScorch(planet, impactWorld, blastRadius, 'mound'),
  };
  const newColors = prevColors ? recolorVertices(newPlanet, prevColors, modifiedVertices) : computePlanetVertexColors(newPlanet);
  return { planet: newPlanet, modifiedVertices, newColors, ...(prevColors ? { recoloured: modifiedVertices } : {}) };
}

// =============================================================================
// EMP EFFECT — disables trajectory preview for affected players
// =============================================================================

export function applyEmpEffect(
  players: PlayerState[],
  planets: PlanetState[],
  impactPoint: Vec3,
  blastRadius: number,
  duration: number
): PlayerState[] {
  const effectRadius = blastRadius * 2.2;
  return players.map(player => {
    if (!player.alive || player.airborne) return player;
    const playerPos = getPlayerWorldPosition(player, planets);
    const dist = vecLength(vecSub(playerPos, impactPoint));
    if (dist < effectRadius) {
      return { ...player, empTurnsRemaining: duration };
    }
    return player;
  });
}

// =============================================================================
// TETHER EFFECT — roots players in radius (movementRemaining = 0)
// =============================================================================

export function applyTetherEffect(
  players: PlayerState[],
  planets: PlanetState[],
  impactPoint: Vec3,
  blastRadius: number
): PlayerState[] {
  const effectRadius = blastRadius * 2.2;
  return players.map(player => {
    if (!player.alive || player.airborne) return player;
    const playerPos = getPlayerWorldPosition(player, planets);
    const dist = vecLength(vecSub(playerPos, impactPoint));
    if (dist < effectRadius) {
      return { ...player, tetheredTurns: 1, movementRemaining: 0 };
    }
    return player;
  });
}

// =============================================================================
// FIRE ZONE — persistent area damage
// =============================================================================

/**
 * Next fire-zone id, from the match state. (A module-level counter outlived its
 * match, so ids — which are in the P2P checksum — depended on what this tab had
 * played before.)
 */
export function nextFireZoneId(zones: readonly FireZone[] | undefined): number {
  let max = 0;
  for (const z of zones ?? []) if (z.id > max) max = z.id;
  return max + 1;
}

export function createFireZone(
  impactPoint: Vec3,
  planetIndex: number,
  radius: number,
  turnsRemaining: number,
  ownerId: number,
  id = 1,
): FireZone {
  return {
    id,
    planetIndex,
    surfacePoint: { ...impactPoint },
    radius,
    turnsRemaining,
    ownerId,
  };
}

export interface FireZoneTick {
  /** Age the zones by one turn and drop the expired ones (once per round). */
  tick?: boolean;
  /** Only these players are burned (the one whose turn is starting); default: everyone standing in fire. */
  burnPlayerIds?: number[];
}

/**
 * Fire zones burn whoever stands in them and burn out after `turnsRemaining`
 * rounds. The resolver ticks them once per round and burns only the player
 * whose turn is starting — previously they ticked and burned on every player
 * turn, so a 2-turn fire was gone before its victim ever acted.
 */
export function processFireZones(
  fireZones: FireZone[],
  players: PlayerState[],
  planets: PlanetState[],
  opts: FireZoneTick = {},
): { fireZones: FireZone[]; players: PlayerState[]; damages: Array<{ playerId: number; damage: number }> } {
  const damages: Array<{ playerId: number; damage: number }> = [];
  const tick = opts.tick ?? true;
  const burn = opts.burnPlayerIds ? new Set(opts.burnPlayerIds) : null;

  const activeZones = tick
    ? fireZones.map(zone => ({ ...zone, turnsRemaining: zone.turnsRemaining - 1 })).filter(zone => zone.turnsRemaining > 0)
    : fireZones;

  // Damage players standing in fire zones
  const newPlayers = players.map(player => {
    if (!player.alive || player.airborne) return player;
    if (burn && !burn.has(player.id)) return player;

    let totalDmg = 0;
    for (const zone of activeZones) {
      if (player.planetIndex !== zone.planetIndex) continue;
      const playerPos = getPlayerWorldPosition(player, planets);
      const dist = vecLength(vecSub(playerPos, zone.surfacePoint));
      if (dist < zone.radius * 2.2) {
        totalDmg += 8; // fireDamagePerTurn
      }
    }

    if (totalDmg > 0) {
      damages.push({ playerId: player.id, damage: totalDmg });
      return damageUnit(player, totalDmg, 'fire').unit;
    }
    return player;
  });

  return { fireZones: activeZones, players: newPlayers, damages };
}

// =============================================================================
// STATUS EFFECT DECAY
// =============================================================================

/**
 * Status "turns" are the victim's own turns. The resolver decays only the
 * player whose turn just ended; with `onlyPlayerId` omitted everyone decays
 * (the old behaviour, which let a 1-turn tether expire before the victim's
 * turn ever came).
 */
export function processEmpDecay(players: PlayerState[], onlyPlayerId?: number): PlayerState[] {
  return players.map(player => {
    if (onlyPlayerId !== undefined && player.id !== onlyPlayerId) return player;
    if (player.empTurnsRemaining && player.empTurnsRemaining > 0) {
      return { ...player, empTurnsRemaining: player.empTurnsRemaining - 1 };
    }
    return player;
  });
}

export function processTetherDecay(players: PlayerState[], onlyPlayerId?: number): PlayerState[] {
  return players.map(player => {
    if (onlyPlayerId !== undefined && player.id !== onlyPlayerId) return player;
    if (player.tetheredTurns && player.tetheredTurns > 0) {
      return { ...player, tetheredTurns: player.tetheredTurns - 1 };
    }
    return player;
  });
}

// =============================================================================
// ANTIPODAL POINT — for drill missile
// =============================================================================

export function computeAntipodal(planet: PlanetState, impactPoint: Vec3): Vec3 {
  const antipodalDir = vecNormalize(vecSub(planet.position, impactPoint));
  const surfRadius = getSurfaceRadius(planet, antipodalDir);
  return vecAdd(planet.position, vecScale(antipodalDir, surfRadius));
}

// =============================================================================
// HEAL
// =============================================================================

export function applyHeal(player: PlayerState, healPercent: number): PlayerState {
  if (!player.alive) return player;
  const newHealth = Math.min(100, player.health + healPercent);
  return { ...player, health: newHealth };
}

/** Which heal item to use: large when badly hurt, otherwise small first. Null when none. */
export function chooseHealItem(player: PlayerState): ItemKey | null {
  const items = player.loadout.items;
  if (!items) return null;
  if (player.health <= 40 && (items.healLarge ?? 0) > 0) return 'healLarge';
  if ((items.healSmall ?? 0) > 0) return 'healSmall';
  if ((items.healLarge ?? 0) > 0) return 'healLarge';
  return null;
}
